import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface Mindmap {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface MindmapNode {
  id: number;
  mindmap_id: number;
  label: string;
  x?: number;
  y?: number;
  color?: string;
  size: number;
  created_at: string;
  updated_at: string;
}

export interface MindmapEdge {
  id: number;
  mindmap_id: number;
  from_node_id: number;
  to_node_id: number;
  label?: string;
  created_at: string;
  updated_at: string;
}

export interface MindmapWithData extends Mindmap {
  nodes: MindmapNode[];
  edges: MindmapEdge[];
}

export interface CreateMindmapData {
  user_id: number;
  title: string;
  description?: string;
}

export interface UpdateMindmapData {
  title?: string;
  description?: string;
}

export interface CreateNodeData {
  label: string;
  x?: number;
  y?: number;
  color?: string;
  size?: number;
}

export interface UpdateNodeData {
  label?: string;
  x?: number;
  y?: number;
  color?: string;
  size?: number;
}

export interface CreateEdgeData {
  from_node_id: number;
  to_node_id: number;
  label?: string;
}

export interface UpdateEdgeData {
  label?: string;
}

export interface MindmapsResponse {
  success: boolean;
  data: Mindmap[];
  count: number;
}

export interface MindmapResponse {
  success: boolean;
  data: MindmapWithData;
  message?: string;
}

export interface SingleMindmapResponse {
  success: boolean;
  data: Mindmap;
  message?: string;
}

export interface NodeResponse {
  success: boolean;
  data: MindmapNode;
  message?: string;
}

export interface EdgeResponse {
  success: boolean;
  data: MindmapEdge;
  message?: string;
}

export class MindmapService {
  /**
   * Get all mindmaps for a user
   */
  static async getUserMindmaps(userId: number, limit: number = 100, offset: number = 0): Promise<MindmapsResponse> {
    const response = await makeAuthenticatedRequest(`/mindmaps/user/${userId}?limit=${limit}&offset=${offset}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch mindmaps');
    }

    return response;
  }

  /**
   * Create a new mindmap
   */
  static async createMindmap(mindmapData: CreateMindmapData): Promise<Mindmap> {
    const response = await makeAuthenticatedRequest('/mindmaps', {
      method: 'POST',
      body: JSON.stringify(mindmapData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create mindmap');
    }

    return response.data;
  }

  /**
   * Get mindmap by ID with nodes and edges
   */
  static async getMindmap(mindmapId: number): Promise<MindmapWithData> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch mindmap');
    }

    return response.data;
  }

  /**
   * Update a mindmap
   */
  static async updateMindmap(mindmapId: number, updateData: UpdateMindmapData): Promise<Mindmap> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update mindmap');
    }

    return response.data;
  }

  /**
   * Delete a mindmap
   */
  static async deleteMindmap(mindmapId: number): Promise<void> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete mindmap');
    }
  }

  /**
   * Create a node in a mindmap
   */
  static async createNode(mindmapId: number, nodeData: CreateNodeData): Promise<MindmapNode> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}/nodes`, {
      method: 'POST',
      body: JSON.stringify(nodeData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create node');
    }

    return response.data;
  }

  /**
   * Update a node
   */
  static async updateNode(mindmapId: number, nodeId: number, updateData: UpdateNodeData): Promise<MindmapNode> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}/nodes/${nodeId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update node');
    }

    return response.data;
  }

  /**
   * Delete a node
   */
  static async deleteNode(mindmapId: number, nodeId: number): Promise<void> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}/nodes/${nodeId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete node');
    }
  }

  /**
   * Create an edge between nodes
   */
  static async createEdge(mindmapId: number, edgeData: CreateEdgeData): Promise<MindmapEdge> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}/edges`, {
      method: 'POST',
      body: JSON.stringify(edgeData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create edge');
    }

    return response.data;
  }

  /**
   * Update an edge
   */
  static async updateEdge(mindmapId: number, edgeId: number, updateData: UpdateEdgeData): Promise<MindmapEdge> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}/edges/${edgeId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update edge');
    }

    return response.data;
  }

  /**
   * Delete an edge
   */
  static async deleteEdge(mindmapId: number, edgeId: number): Promise<void> {
    const response = await makeAuthenticatedRequest(`/mindmaps/${mindmapId}/edges/${edgeId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete edge');
    }
  }
}

export default MindmapService;

